// 인증 / 세션
//
// store/index.js 가 이 조각들을 펼쳐 하나의 루트 스토어로 합친다.
// Vuex 의 modules 옵션이 아니라 "조각"인 이유: Vuex 는 namespaced 여부와 무관하게
// 모듈 state 를 모듈 이름 아래에 중첩시킨다. 진짜 모듈로 바꾸면 store.state.tasks 가
// store.state.tasks.tasks 가 되어 소비자 40곳을 전부 고쳐야 한다.
// 조각 합성은 동작이 100% 동일하면서 파일만 나눈다.

import http from '@/utils/http'

/** 깨진 값(손으로 고친 JSON 등) 하나로 앱이 첫 화면에서 죽지 않게 한다 — 읽지 못하면 로그아웃 상태로 시작한다 */
function storedUser() {
  try {
    return JSON.parse(localStorage.getItem('user')) || null
  } catch (e) {
    return null
  }
}

export const state = {
  user: storedUser()
}

export const mutations = {
  SET_USER(state, user) {
    state.user = user
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    } else {
      localStorage.removeItem('user')
    }
  }
}

export const actions = {
  async checkAuth({ commit }) {
    try {
      const response = await http.get(`/api/auth/status`)
      commit('SET_USER', response.data.user)
      return response.data
    } catch (error) {
      commit('SET_USER', null)
      throw error
    }
  },

  /** checkAuth 와 같지만 실패해도 사용자를 지우지 않는다 — 앱을 열 때 권한 변화만 반영하려는 용도(AppShell) */
  async refreshUser({ commit }) {
    try {
      const response = await http.get(`/api/auth/status`)
      if (response.data?.user) commit('SET_USER', response.data.user)
    } catch (error) {
      // 401 이면 main.js 의 응답 가로채기가 로그인 화면으로 보낸다. 네트워크 오류로는 로그아웃시키지 않는다.
    }
  },

  /**
   * 로그아웃. 세션 만료 등으로 요청이 실패해도 클라이언트 상태는 비운다.
   * 스토어를 조각조각 비우지 않고 로그인 화면을 **새로 불러온다**(confirmLogout) — 업무 · 영업 · 알림 · 인사처럼
   * 화면마다 들고 있는 메모리가 여러 곳이라, 몇 개만 지우면 다음 사람이 앞 사람의 목록을 잠깐 보게 된다.
   */
  async logout({ commit }) {
    try {
      await http.post(`/api/auth/logout`)
    } catch (error) {
      // 이미 끊긴 세션이면 실패한다 — 어차피 클라이언트 상태는 비운다
    } finally {
      commit('SET_USER', null)
    }
  }
}

export const getters = {
  isAuthenticated: (state) => !!state.user,
  // 관리자 화면 · 조직도 · 바로가기 편집. 서버 requireAdmin 과 같은 기준(isAdmin 또는 grade 3 이상)
  isAdmin: (state) => !!state.user?.isAdmin || (state.user?.grade || 0) >= 3,
  currentUser: (state) => state.user,
  canReport: (state) => (state.user?.grade || 0) >= 2,
  // 인사관리 — 서버가 /api/auth/status 에 실어 주는 hrAccess(services/hrAccess.js). 중요관리자 이상(grade 3 · 4)은 늘 true
  canHr: (state) => !!state.user?.hrAccess || (state.user?.grade || 0) >= 3
}
